<template>
  <div v-bind="$attrs">
    <label>
      {{ label }}
      <v-input
        :model-value="modelValue"
        :rules="rules"
        :error="!!error"
        :error-messages="error ? [error] : undefined"
        :disabled="disabled"
        hide-details="auto"
        class="mb-1"
        validate-on="blur"
      >
        <div class="code-editor" :style="{ height }">
          <div class="code-editor__scroll">
            <pre
              class="code-editor__highlight"
              :class="`language-${language}`"
              aria-hidden="true"
            ><code v-html="highlighted"></code></pre>
            <textarea
              :value="modelValue ?? ''"
              :readonly="readonly"
              :disabled="disabled"
              :placeholder="placeholder"
              class="code-editor__input"
              spellcheck="false"
              autocapitalize="off"
              autocomplete="off"
              :data-testid="dataTestId"
              @input="onInput"
            ></textarea>
          </div>
        </div>
      </v-input>
    </label>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { ValidationRule } from "@/types/ValidationRule"
  import { highlightCode, CodeLanguage } from "@/helpers/codeHighlightHelper"

  export default defineComponent({
    inheritAttrs: false,
    props: {
      modelValue: {
        type: String as PropType<string | null>,
        default: null,
      },
      label: {
        type: String,
        default: null,
      },
      language: {
        type: String as PropType<CodeLanguage>,
        default: "markup",
      },
      rows: {
        type: Number,
        default: 18,
      },
      rules: {
        type: Array as PropType<ValidationRule[]>,
        default: () => [] as ValidationRule[],
      },
      error: {
        type: String,
        default: null,
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      placeholder: {
        type: String,
        default: undefined,
      },
      dataTestId: {
        type: String,
        default: undefined,
      },
    },
    emits: ["update:modelValue"],
    computed: {
      // em resolves against the field's own font size, which is the code size.
      height(): string {
        return `${this.rows * 1.5}em`
      },
      // The trailing newline keeps the highlight layer as tall as the textarea
      // while the caret sits on a freshly opened last line.
      highlighted(): string {
        return highlightCode(`${this.modelValue ?? ""}\n`, this.language)
      },
    },
    methods: {
      onInput(event: Event) {
        this.$emit(
          "update:modelValue",
          (event.target as HTMLTextAreaElement).value,
        )
      },
    },
  })
</script>

<style scoped>
  .code-editor {
    --code-editor-font-size: 0.8125rem;
    --code-editor-line-height: 1.5;
    /* A flex item inside v-input's control, so without this the field is only
       as wide as its longest line — and invisible while it is empty. */
    width: 100%;
    /* The two layers below inherit it, so `rows` counts lines of code. */
    font-size: var(--code-editor-font-size);
    min-width: 0;
    overflow: auto;
    border: 1px solid rgb(var(--v-theme-border));
    border-radius: var(--theme-radius);
    transition: border 250ms;
  }
  .code-editor:hover {
    border: 1px solid rgb(var(--v-theme-on-surface));
  }
  .code-editor:focus-within {
    outline: var(--theme-outline);
  }
  /* Column flex so the highlight layer stretches to the full field height and
     its Prism theme background covers the field, not just the typed lines. */
  .code-editor__scroll {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }
  /* The highlight layer sets the height; the textarea stretches over it, so the
     two must share every metric that affects wrapping. */
  .code-editor__highlight,
  .code-editor__input {
    margin: 0;
    padding: 12px;
    font-family: monospace;
    font-size: var(--code-editor-font-size);
    line-height: var(--code-editor-line-height);
    white-space: pre-wrap;
    overflow-wrap: break-word;
    tab-size: 2;
  }
  .code-editor__highlight {
    flex: 1 0 auto;
    pointer-events: none;
    background: var(--code-background, rgb(var(--v-theme-surface)));
  }
  .code-editor__input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    color: transparent;
    caret-color: rgb(var(--v-theme-on-surface));
    background: transparent;
    border: none;
    resize: none;
    outline: none;
    overflow: hidden;
  }
  .code-editor__input::placeholder {
    color: rgb(var(--v-theme-on-surface));
    opacity: 0.5;
  }
</style>
